import axios from "axios"
import produce from "immer"
import md5 from "md5"
import { END, eventChannel } from "redux-saga"
import { call, fork, put, select, take, takeLatest } from "redux-saga/effects"
import { uploadStatus } from "../../constants"
import { OrderInfo } from "../../utils/selector"
import { isNotNullOrEmpty } from "../../utils/validate"
import { actionTypes, makeAction } from "../actions"
import api from "../apiServices"

export let uploadSagas = [takeLatest(actionTypes.UPLOAD_FILE, uploadFile), takeLatest(actionTypes.CANCEL_UPLOAD_FILE, cancelFileUpload)]

const uploadFileStart = () => makeAction(subActions.UPLOAD_FILES_START)
const uploadFileSuccess = (result) => makeAction(subActions.UPLOAD_FILES_SUCCESS, { result })
const uploadFileFailed = (error) => makeAction(subActions.UPLOAD_FILES_FAILED, { error })
const uploadFileProgress = (progress) => makeAction(subActions.UPLOAD_FILES_PROGRESS, { progress })

const initialState = {
  status: uploadStatus.init,
  error: false,
  progress: 0,
  file: {
    result: {}
  }
}

const CancelToken = axios.CancelToken
let cancel

const subActions = {
  UPLOAD_FILES_START: "UPLOAD_FILES_START",
  UPLOAD_FILES_PROGRESS: "UPLOAD_FILES_PROGRESS",
  UPLOAD_FILES_SUCCESS: "UPLOAD_FILES_SUCCESS",
  UPLOAD_FILES_FAILED: "UPLOAD_FILES_FAILED"
}

export default (state = initialState, action) =>
  produce(state, (draft) => {
    switch (action.type) {
      case actionTypes.UPLOAD_FILE:
        Object.assign(draft, {
          status: uploadStatus.pending,
          error: false,
          progress: 0,
          file: action.payload.file
        })
        break
      case subActions.UPLOAD_FILES_START:
        Object.assign(draft, {
          status: uploadStatus.inProgress
        })
        break
      case subActions.UPLOAD_FILES_PROGRESS:
        Object.assign(draft, {
          progress: action.payload.progress
        })
        break
      case subActions.UPLOAD_FILES_SUCCESS:
        Object.assign(draft, {
          status: uploadStatus.done
        })
        break
      case subActions.UPLOAD_FILES_FAILED:
        Object.assign(draft, {
          status: uploadStatus.error,
          error: action.payload.error
        })
        break
    }
  })

function createUploader({ endpoint, formData, params, cancelToken }) {
  let emit
  const channel = eventChannel((emitter) => {
    emit = emitter
    return () => {}
  })

  const uploadProgress = ({ total, loaded }) => {
    const percentage = Math.round((loaded * 100) / total)
    emit(percentage)
    if (percentage === 100) emit(END)
  }
  const uploadPromise = api.upload(endpoint, formData, uploadProgress, params, cancelToken)
  return [uploadPromise, channel]
}

function* uploadProgressWatcher(channel) {
  while (true) {
    const progress = yield take(channel)
    yield put(uploadFileProgress(progress))
  }
}

function appendFilenameAndExtParams(params, filename, contractID) {
  if (filename) {
    let sections = filename.split(".")
    let ext = sections.pop()
    let contractIDSection = contractID?.length ? `_${contractID}` : ""
    params.filename = `${sections.join(".")}_${Date.now()}${contractIDSection}.${ext}`
    params.ext = ext
  }
  return params
}

export function getContractIDFrom(orderID) {
  if (!orderID?.length) return ""
  return orderID.split("-")[0]
}

function* uploadFile({ payload: { file, onSuccessAction, onFailureAction } }) {
  log("Upload File Start")
  const fileName = file.name
  yield put(uploadFileStart())

  const orderInfo = yield select(OrderInfo)

  try {
    const brID = orderInfo?.contract?.br?.id || orderInfo?.br?.id
    if (!brID) {
      throw new Error("BR ID is missing")
    }

    let params = {}
    yield call(appendFilenameAndExtParams, params, fileName, getContractIDFrom(orderInfo.id))

    const ext = params.ext?.toLowerCase()

    const docFileType = ext === "jpg" || ext === "jpeg" ? "image/jpeg" : ext === "png" ? "image/png" : ext === "pdf" ? "application/pdf" : "application/octet-stream"
    const fileBuffer = yield file.arrayBuffer()
    const buffer = Buffer.from(fileBuffer)
    const hash = md5(buffer)

    // Get Presign Url
    const { data: presignedJson } = yield call(() => api.create("/br_upload_url", {
      filename: params.filename,
      ext: ext,
      contentLength: file.size
    }))
    const { objectKey, url: presignedUrl} = presignedJson;

    // Upload with progress tracking
    let emit
    const channel = eventChannel((emitter) => {
      emit = emitter
      return () => {}
    })

    const uploadProgress = (progressEvent) => {
      const percentage = Math.round((progressEvent.loaded * 100) / progressEvent.total)
      emit(percentage)
      if (percentage === 100) emit(END)
    }

    yield fork(uploadProgressWatcher, channel)

    yield call(() => axios.put(presignedUrl, buffer, {
      headers: {
        'Content-Type': docFileType,
        'Content-Length': file.size,
      },
      maxBodyLength: Infinity,
      maxContentLength: Infinity,
      onUploadProgress: uploadProgress
    }))

    const { data: res } = yield call(() => api.create(`/brs/${brID}/notify_upload`, {
      objectKey: objectKey,
      signature: hash
    }))

    log(res)
    if (isNotNullOrEmpty(res)) {
      yield put(uploadFileSuccess())
      yield call(onSuccessAction, res)
    }
  } catch (err) {
    log(`Error ${err}`)
    yield put(uploadFileFailed(err))
    yield call(onFailureAction, err)
  }
  log("Upload File End")
}

function* cancelFileUpload() {
  log("Cancel File Upload Start")
  cancel && cancel()
  log("Cancel File Upload End")
}

function log(text) {
  console.log("%c Upload ", "color: white; background-color: #fb5004", text)
}
